import { getAuth } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';

const state = {
  ethicalPreferences: {
    laborPractices: 5,
    environmentalImpact: 5,
    animalWelfare: 5,
    supplyChainTransparency: 5,
    corporateEthics: 5,
    communityImpact: 5,
    productSafety: 5,
    marketingHonesty: 5,
    culturalSensitivity: 5,
    sustainablePackaging: 5,
    energyConsumption: 5,
    dataPrivacy: 5,
    inclusivity: 5,
    plannedObsolescence: 5
  },
  politicalPreferences: {
    importanceOfPoliticalContributions: 5,
    democraticContributions: 5,
    republicanContributions: 5,
    corporateTransparency: 5
  },
  weightPreferences: {
    price: 5,
    quality: 5,
    ethical: 5,
    political: 0
  }
};

const getters = {
  getEthicalPreferences: state => state.ethicalPreferences,
  getPoliticalPreferences: state => state.politicalPreferences,
  getWeightPreferences: state => state.weightPreferences,
  getAllPreferences: state => ({
    ethical: state.ethicalPreferences,
    political: state.politicalPreferences,
    weights: state.weightPreferences
  })
};

const mutations = {
  setEthicalPreference(state, { key, value }) {
    state.ethicalPreferences[key] = value;
  },
  setPoliticalPreference(state, { key, value }) {
    state.politicalPreferences[key] = value;
  },
  setWeightPreference(state, { key, value }) {
    state.weightPreferences[key] = value;
  },
  setAllPreferences(state, { ethical, political, weights }) {
    if (ethical) state.ethicalPreferences = { ...state.ethicalPreferences, ...ethical };
    if (political) state.politicalPreferences = { ...state.politicalPreferences, ...political };
    if (weights) state.weightPreferences = { ...state.weightPreferences, ...weights };
  },
  updateEthicalPreferences(state, preferences) {
    state.ethicalPreferences = { ...state.ethicalPreferences, ...preferences };
  },
  updatePoliticalPreferences(state, preferences) {
    state.politicalPreferences = { ...state.politicalPreferences, ...preferences };
  },
  updateWeightPreferences(state, preferences) {
    state.weightPreferences = { ...state.weightPreferences, ...preferences };
  }
};

const actions = {
  async savePreferences({ state }) {
    // Get the authenticated user
    const auth = getAuth();
    const user = auth.currentUser;
    
    // Add more detailed logging for debugging
    console.log('savePreferences called - Auth state:', user ? 'Authenticated' : 'Not authenticated');

    if (!user) {
      console.error('Cannot save preferences - user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    try {
      console.log(`Attempting to save preferences for user ${user.uid}`);
      
      // Use direct Firestore V9 method - more reliable with region changes
      const userPrefRef = doc(db, 'userPreferences', user.uid);
      
      // Combine all preferences into a single object
      const preferencesData = {
        ethicalPreferences: state.ethicalPreferences,
        politicalPreferences: state.politicalPreferences,
        weightPreferences: state.weightPreferences,
        timestamp: new Date().toISOString(),
        updatedBy: user.email || user.uid
      };
      
      // Use merge: true to avoid overwriting any other fields
      await setDoc(userPrefRef, preferencesData, { merge: true });
      
      console.log('✅ Preferences saved successfully');
      return { success: true };
    } catch (error) {
      console.error('Failed to save preferences:', error);
      
      // Add debug info for permission errors
      if (error.code === 'permission-denied') {
        console.error('This is a permissions issue - checking token details:');
        
        // Log token information for debugging
        try {
          const token = await user.getIdToken(true);  // Force refresh
          const tokenLength = token ? token.length : 'No token';
          console.log(`Token refreshed, length: ${tokenLength}`);
          console.log(`User email verified: ${user.emailVerified}`);
        } catch (tokenError) {
          console.error('Error refreshing token:', tokenError);
        }
      }
      
      return { 
        success: false, 
        error: error.message,
        code: error.code 
      };
    }
  },
  
  async loadPreferences({ commit }) {
    // Get the authenticated user
    const auth = getAuth();
    const user = auth.currentUser;
    
    console.log('preferences/loadPreferences called - Auth state:', user ? 'Authenticated' : 'Not authenticated');

    if (!user) {
      console.error('Cannot load preferences - user not authenticated');
      return { success: false, error: 'User not authenticated' };
    }

    try {
      console.log(`Attempting to load preferences for user ${user.uid}`);
      
      // Use direct Firestore V9 method for better region handling
      const userPrefRef = doc(db, 'userPreferences', user.uid);
      
      // Get the document
      const docSnap = await getDoc(userPrefRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log('Preferences loaded successfully:', data);
        
        // Update state with loaded preferences
        if (data.ethicalPreferences) {
          commit('updateEthicalPreferences', data.ethicalPreferences);
          console.log('Updated ethical preferences with database values');
        } else {
          console.log('No ethical preferences found in database, using defaults');
        }
        
        if (data.politicalPreferences) {
          commit('updatePoliticalPreferences', data.politicalPreferences);
          console.log('Updated political preferences with database values');
        } else {
          console.log('No political preferences found in database, using defaults');
        }
        
        if (data.weightPreferences) {
          commit('updateWeightPreferences', data.weightPreferences);
          console.log('Updated weight preferences with database values');
        } else {
          console.log('No weight preferences found in database, using defaults');
        }
        
        return { success: true, data };
      } else {
        console.log('No preferences document found for user, using defaults');
        return { success: true, data: null };
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
          
      // Additional debug info for permission issues
      if (error.code === 'permission-denied') {
        console.error('This is a permissions issue - checking token and rules:');
        try {
          // Test token validity
          const token = await user.getIdToken(true);  // Force refresh
          console.log('Token refreshed successfully, length:', token ? token.length : 'No token');
          // Test direct access for debugging
          window.testCurrentUserDocument?.();
        } catch (testError) {
          console.error('Error handling permission issue:', testError);
        }
      }
      
      return { 
        success: false, 
        error: error.message,
        code: error.code 
      };
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}; 